import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { routeTree } from "./routeTree.gen";
import { queryClient } from "@/lib/query";
import { useAuthStore, bindAuthChannelSync } from "@/stores/auth";
import { useInstanceStore } from "@/stores/instance";
import { ToastProvider } from "@/components/ui/toast";

export const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: "intent",
  defaultStructuralSharing: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

/**
 * Boots the app: loads the public instance profile, attempts session restore,
 * binds cross-tab auth sync, and starts realtime once authenticated.
 */
export function App() {
  const instanceStatus = useInstanceStore((s) => s.status);
  const loadInstance = useInstanceStore((s) => s.load);
  const restore = useAuthStore((s) => s.restore);

  const booted = useRef(false);
  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    void loadInstance();
    void restore();
    const unbind = bindAuthChannelSync();
    return unbind;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        {instanceStatus === "error" ? <BootError /> : <RouterProvider router={router} />}
      </ToastProvider>
    </QueryClientProvider>
  );
}

function BootError() {
  const reload = useInstanceStore((s) => s.reload);
  const error = useInstanceStore((s) => s.error);
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="text-base font-medium">Couldn't reach the Memos server</p>
      <p className="max-w-sm text-sm text-fg-mute">{error ?? "Check that the backend is running and try again."}</p>
      <button
        type="button"
        onClick={() => void reload()}
        className="mt-2 h-9 rounded-md bg-accent px-4 text-sm font-medium text-accent-fg"
      >
        Retry
      </button>
    </div>
  );
}
